import { SUB_SKU_SUFFIX } from '@/constants/commerce'
import type {
  CMSBuyingPanelOption,
  CMSProductCardVariant,
  CMSProductVariant,
} from '@/types/sanity'
import type {
  Cart,
  ProductSellingPlans,
  SellingPlanAllocation,
  SellingPlanVariant,
} from '@/types/shopify'
import Cookie from 'js-cookie'
import { CART_STORAGE_KEY } from '@/constants/shopify'

export const getCartZipCode = (cart: Cart) => {
  const zipCodes = cart.lines.reduce((codes, line) => {
    const zipCodeAttr = line.attributes.find((attr) => attr.key === '_zip_code')
    if (!zipCodeAttr) return codes
    codes.push(zipCodeAttr.value)
    return codes
  }, [] as string[])
  return zipCodes[0]
}

export const filterOutRelatedVariants = (
  variants: CMSProductVariant[],
): CMSProductVariant[] => {
  const subscriptionVariants = variants.filter((variant) =>
    variantIsSubscription(variant.sku),
  )

  if (!subscriptionVariants?.length) return variants

  const hasRelatedOTPVariant = subscriptionVariants.filter((subVariant) =>
    variants.find(
      (variant) => `${variant.sku}${SUB_SKU_SUFFIX}` === subVariant.sku,
    ),
  )

  return variants?.filter(
    (variant) =>
      !hasRelatedOTPVariant.find(
        (dupVariant) => variant.sku === dupVariant.sku,
      ),
  )
}

export const variantIsSubscription = (sku: string) =>
  sku?.endsWith(SUB_SKU_SUFFIX)

export const variantIsAvailableForSale = (
  variant: CMSProductVariant | CMSProductCardVariant,
) => {
  if (variant.inventory.management === 'NOT_MANAGED') return true
  if (variant.inventory.policy === 'CONTINUE') return true

  return variant.inventory.isAvailable
}

export const isProductCardVariantEnabled = (variant: CMSProductCardVariant) => {
  return variant.isActive && !variant.isHidden && !variant.isDisabled
}

export const mapSellingPlansToVariants = (
  variants: CMSProductVariant[],
  sellingPlans: ProductSellingPlans | null,
): CMSProductVariant[] => {
  if (
    !variants?.length ||
    !sellingPlans ||
    !sellingPlans?.sellingPlanGroups?.length
  ) {
    return variants
  }

  const sellingPlanBySku = sellingPlans.variants.reduce(
    (acc, plan: SellingPlanVariant) => {
      acc[plan.sku] = plan.sellingPlanAllocations?.length
        ? plan.sellingPlanAllocations[0].sellingPlan
        : null

      return acc
    },
    {} as Record<string, SellingPlanAllocation['sellingPlan'] | null>,
  )

  return variants.map((variant: CMSProductVariant) => {
    const newVariant = variant
    const sellingPlan = sellingPlanBySku[variant.sku]

    if (sellingPlan) {
      newVariant.sellingPlan = sellingPlan
    }

    return newVariant
  })
}

export const enhanceBuyingPanelOptionSizesWithSellingPlan = (
  sizes: { variant: CMSProductVariant }[],
  mappedVariants: CMSProductVariant[],
): { variant: CMSProductVariant }[] => {
  return sizes.reduce<{ variant: CMSProductVariant }[]>((acc, size) => {
    const mappedVariant = mappedVariants.find(
      (mappedVariant) => mappedVariant.id === size.variant.id,
    )

    if (!mappedVariant) return acc

    acc.push({
      ...size,
      variant: mappedVariant,
    })

    return acc
  }, [])
}

export const enhanceBuyingPanelOptionsWithSellingPlan = (
  options: CMSBuyingPanelOption[],
  mappedVariants: CMSProductVariant[],
): CMSBuyingPanelOption[] => {
  return options.reduce<CMSBuyingPanelOption[]>((acc, option) => {
    const mappedVariant = mappedVariants.find(
      (mappedVariant) => mappedVariant.id === option.variant.id,
    )

    if (!mappedVariant) return acc

    acc.push({
      ...option,
      variant: mappedVariant,
      sizes:
        option.sizes &&
        enhanceBuyingPanelOptionSizesWithSellingPlan(
          option.sizes,
          mappedVariants,
        ),
    })

    return acc
  }, [])
}

export const isHiddenShopifyProduct = (tags: string) => {
  const tagsArr = tags.split(',')

  if (!tagsArr.length) {
    return false
  }

  if (
    tagsArr.includes('hidden') ||
    tagsArr.includes('hide-add-to-cart') ||
    tagsArr.includes('hidden-from-search')
  ) {
    return true
  }

  return false
}

export const transformObjectToCartItemAttributes = (
  object: Record<string, any>,
) => {
  return Object.entries(object).map(([key, value]) => ({
    key,
    value: value.toString(),
  }))
}

export const gql = String.raw

export const simpleCartToken = (
  (Cookie.get(CART_STORAGE_KEY) || '').split('/').pop() || ''
).split('?')[0]
